import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import './Card.css';
import '../TypingText/TypingText.css';

const Card = ({ card, price, hideDetails, isHomepage = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const certNumber = card?.cert_number;
    const [hovered, setHovered] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [flavorTextDisplay, setFlavorTextDisplay] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const typingInterval = useRef(null);
    const flavorText = card?.card?.flavor_text;
    
    // Check if we're on a main listing page
    const isMainListingPage = ['/pokemon', '/trainer', '/energy', '/promo', '/sets'].some(path => 
        location.pathname.startsWith(path)
    );

    // Detect mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 600);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Global touch end listener to ensure panes close
    useEffect(() => {
        if (!isMobile) return;

        const handleGlobalTouchEnd = () => {
            // Close all panes when any touch ends
            setHovered(false);
            setFlavorTextDisplay('');
            if (typingInterval.current) clearInterval(typingInterval.current);
        };

        document.addEventListener('touchend', handleGlobalTouchEnd);
        document.addEventListener('touchcancel', handleGlobalTouchEnd);
        
        return () => {
            document.removeEventListener('touchend', handleGlobalTouchEnd);
            document.removeEventListener('touchcancel', handleGlobalTouchEnd);
        };
    }, [isMobile]);

    const handleClick = () => {
        navigate(`/listing/${certNumber}`);
    };

    const handleMouseEnter = () => {
        // Don't show hover effects on mobile
        if (isMobile) return;
        
        setHovered(true);
        if (flavorText) {
            setFlavorTextDisplay('');
            if (typingInterval.current) clearInterval(typingInterval.current);
            let i = 0;
            typingInterval.current = setInterval(() => {
                setFlavorTextDisplay((prev) => prev + flavorText[i]);
                i++;
                if (i === flavorText.length) clearInterval(typingInterval.current);
            }, 30);
        }
    };

    const handleMouseLeave = () => {
        // Don't show hover effects on mobile
        if (isMobile) return;
        
        setHovered(false);
        if (flavorText) {
            setFlavorTextDisplay('');
            if (typingInterval.current) clearInterval(typingInterval.current);
        }
    };

    const handleMouseMove = (e) => {
        // Don't track mouse position on mobile
        if (isMobile) return;
        
        setMousePos({ x: e.clientX, y: e.clientY });
    };

    // Mobile touch handlers for flavor text
    const handleTouchStart = (e) => {
        if (!isMobile) return;
        
        const touch = e.touches[0];
        setMousePos({ x: touch.clientX, y: touch.clientY });
        
        // Add small delay to prevent showing on quick taps
        setTimeout(() => {
            // Show enlarged image for all cards on mobile (except homepage)
            if (!isHomepage) {
                setHovered(true);
            }
            
            // Show flavor text if available (on all pages including homepage)
            if (flavorText) {
                setHovered(true);
                setFlavorTextDisplay('');
                if (typingInterval.current) clearInterval(typingInterval.current);
                let i = 0;
                typingInterval.current = setInterval(() => {
                    setFlavorTextDisplay((prev) => prev + flavorText[i]);
                    i++;
                    if (i === flavorText.length) clearInterval(typingInterval.current);
                }, 30);
            }
        }, 150); // 150ms delay - just longer than a quick tap
    };

    const handleTouchMove = (e) => {
        if (!isMobile || !hovered) return;
        
        const touch = e.touches[0];
        setMousePos({ x: touch.clientX, y: touch.clientY });
    };

    const handleTouchEnd = (e) => {
        if (!isMobile) return;
        
        // Hide enlarged image and flavor text when touch ends
        setHovered(false);
        setFlavorTextDisplay('');
        if (typingInterval.current) clearInterval(typingInterval.current);
    };

    // --- Deal Grade Calculation ---
    let dealGrade = '--';
    let priceChartingValue = null;
    const cardGrade = card?.grade || card?.cert?.grade;
    const pricingData = card?.pricing_data;
    if (pricingData && cardGrade) {
        // Try to match the grade to a pricecharting field
        let field = null;
        if (typeof cardGrade === 'string') {
            if (cardGrade.includes('10')) field = 'psa_10';
            else if (cardGrade.includes('9')) field = 'psa_9';
            else if (cardGrade.includes('8')) field = 'psa_8';
            else if (cardGrade.includes('7')) field = 'psa_7';
        } else if (typeof cardGrade === 'number') {
            field = `psa_${cardGrade}`;
        }
        if (field && pricingData[field]) {
            priceChartingValue = pricingData[field];
            const diff = price - priceChartingValue;
            const pct = diff / priceChartingValue;
            if (pct <= 0) dealGrade = 'A+';
            else if (pct <= 0.05) dealGrade = 'A';
            else if (pct <= 0.10) dealGrade = 'A-';
            else if (pct <= 0.15) dealGrade = 'B+';
            else if (pct <= 0.20) dealGrade = 'B';
            else if (pct <= 0.25) dealGrade = 'B-';
            else if (pct <= 0.30) dealGrade = 'C+';
            else if (pct <= 0.35) dealGrade = 'C';
            else if (pct <= 0.40) dealGrade = 'C-';
            else dealGrade = '--';
        }
    }

    const frontImageUrl = card?.cert_images?.front_image_url || 'https://via.placeholder.com/200';
    const name = card?.card?.name || 'Unknown';
    const finalPrice = Math.ceil(price); // Adjust based on your pricing logic

    return (
        <>
            <div
                className={`card${!isHomepage ? ' not-homepage' : ''}`}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ position: 'relative', cursor: 'pointer' }}
            >
            <img className="card-image" src={frontImageUrl} alt={name} />
            {/* Deal Grade Badge */}
            <span className={`deal-grade-badge${isHomepage ? ' homepage-badge' : ''}`} data-grade={dealGrade}>
              <span className="deal-grade-letter">{dealGrade}</span>
              <span className="deal-grade-label">DEAL</span>
            </span>
            {!hideDetails && (
                <div className="card-details">
                    <div className="card-info">
                        <span className="card-name">{name}</span>
                        <span className="card-price">${finalPrice.toLocaleString()}</span>
                    </div>
                </div>
            )}
        </div>
            
            {/* Mobile enlarged image and flavor text */}
            {hovered && isMobile && !isHomepage && createPortal(
                <div 
                    className="mobile-card-preview"
                    style={{
                        position: 'fixed',
                        left: mousePos.x < window.innerWidth / 2 
                            ? 'auto' // Left side: right-justified
                            : '20px', // Right side: left-justified
                        right: mousePos.x < window.innerWidth / 2 
                            ? '20px' // Left side: right-justified
                            : 'auto', // Right side: left-justified
                        top: '80px', // Closer to header
                        zIndex: 9999,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        padding: '12px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <img 
                        src={frontImageUrl} 
                        alt={name} 
                        style={{
                            maxWidth: '260px',
                            maxHeight: '360px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                        }}
                    />
                </div>,
                document.body
            )}
            
            {/* Mobile flavor text in separate pane */}
            {hovered && flavorText && isMobile && createPortal(
                <div 
                    className="mobile-flavor-text-pane"
                    style={{
                        position: 'fixed',
                        left: mousePos.x < window.innerWidth / 2 
                            ? 'auto' // Left side: right-justified
                            : '20px', // Right side: left-justified
                        right: mousePos.x < window.innerWidth / 2 
                            ? '20px' // Left side: right-justified
                            : 'auto', // Right side: left-justified
                        top: isHomepage ? '100px' : '470px', // Closer to image pane
                        zIndex: 9999,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        padding: '12px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        minWidth: '100px',
                        maxWidth: '240px', // Shrunk to be narrower than image pane
                        fontSize: '0.9rem',
                        lineHeight: '1.4',
                        textAlign: 'center',
                        fontFamily: 'Courier New, Courier, monospace',
                        letterSpacing: '0.02em'
                    }}
                >
                    <p style={{ 
                        fontSize: '0.9rem', 
                        margin: '0', 
                        padding: '0',
                        fontFamily: 'Courier New, Courier, monospace',
                        lineHeight: '1.4',
                        letterSpacing: '0.02em',
                        color: '#333'
                    }}><i>{flavorTextDisplay}</i></p>
                </div>,
                document.body
            )}
            
            {/* Desktop flavor text only */}
            {hovered && flavorText && !isMobile && createPortal(
                <div 
                    className="flavor-text-box"
                    style={{
                        position: 'fixed',
                        left: mousePos.x + 20,
                        top: mousePos.y + 20,
                        zIndex: 9999,
                        minWidth: 198,
                        maxWidth: 300,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        padding: '12px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        fontSize: '1rem',
                        lineHeight: '1.4'
                    }}
                >
                    <p className="flavor-text"><i>{flavorTextDisplay}</i></p>
                </div>,
                document.body
            )}
            
            {/* Desktop preview pane */}
            {isMainListingPage && hovered && !isMobile && createPortal(
                <div 
                    className="card-preview-pane"
                    style={{
                        position: 'fixed',
                        left: mousePos.x < window.innerWidth / 2 ? 'auto' : '20px',
                        right: mousePos.x < window.innerWidth / 2 ? '20px' : 'auto',
                        top: '80px',
                        bottom: '20px',
                        backgroundColor: 'white',
                        zIndex: 9998,
                        padding: '20px',
                        borderRadius: '15px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <img 
                        src={frontImageUrl} 
                        alt={name} 
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            borderRadius: '10px'
                        }}
                    />
                </div>,
                document.body
            )}
        </>
    );
};

export default Card;
